import { useHotkeys, Options } from "react-hotkeys-hook";
import { Shape, WallShape } from "../../../../../types";
import { createWall } from "../../../../../utils/shapeFactory";
import { findMissingRectWalls } from "../../../../../utils/wallRect";
import { useMapCanvas } from "../../../contexts/MapCanvasContext";
import { useMapEditorActions } from "../../../contexts/MapEditorContext";
import { useMapEditorSelection } from "../../../contexts/MapEditorContext";
import { useEditorShortcuts } from "../EditorShortcutContext/useEditorShortcuts";
import { SHAPE_SELECTION_SHORTCUTS_SCOPE } from "../shortcuts";

declare global {
  interface EyeDropper {
    open(): Promise<{ sRGBHex: string }>;
  }
  interface EyeDropperConstructor {
    new (): EyeDropper;
  }
  interface Window {
    EyeDropper?: EyeDropperConstructor;
  }
}

export const useShapeSelectionShortcuts = () => {
  const { selectedIds } = useMapEditorSelection();
  const {
    deleteShape,
    updateShapes,
    duplicateShape,
    setSelectedIds,
    copySelectedShapes,
    setShapes,
    updateShape,
  } = useMapEditorActions();
  const { shapesRef } = useMapCanvas();
  const { shortcuts } = useEditorShortcuts();
  const enabled = selectedIds.length > 0;
  const scopes = [SHAPE_SELECTION_SHORTCUTS_SCOPE];
  const preventDefault = true;

  const shortcutOptions: Options = { scopes, enabled, preventDefault };

  const getFirstShape = (event: KeyboardEvent) => {
    event.preventDefault();
    event.stopPropagation();
    return shapesRef.current.find((shape) => shape.id === selectedIds[0]);
  };

  useHotkeys(
    shortcuts.shapeSelection.delete,
    () => {
      deleteShape(selectedIds);
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.delete],
  );

  useHotkeys(
    shortcuts.shapeSelection.showHide,
    (event) => {
      const firstShape = getFirstShape(event);
      if (!firstShape) return;
      updateShapes(selectedIds, { hidden: !firstShape.hidden });
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.showHide],
  );

  useHotkeys(
    shortcuts.shapeSelection.lockUnlock,
    (event) => {
      const firstShape = getFirstShape(event);
      if (!firstShape) return;

      updateShapes(selectedIds, { locked: !firstShape.locked });
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.lockUnlock],
  );

  useHotkeys(
    shortcuts.shapeSelection.duplicate,
    (event) => {
      const firstShape = getFirstShape(event);
      if (!firstShape) return;

      const newShapes = duplicateShape(selectedIds);

      if (newShapes) setSelectedIds(newShapes.map((shape) => shape.id));
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.duplicate],
  );

  useHotkeys(
    shortcuts.shapeSelection.cut,
    (event) => {
      const firstShape = getFirstShape(event);
      if (!firstShape) return;

      copySelectedShapes();
      deleteShape(selectedIds);
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.cut],
  );

  useHotkeys(
    shortcuts.shapeSelection.closeWallRect,
    () => {
      const selectedWalls = selectedIds
        .map((id) => shapesRef.current.find((s) => s.id === id))
        .filter((s): s is WallShape => s?.type === "wall");

      if (selectedWalls.length < 2) return;

      const missingSides = findMissingRectWalls(selectedWalls);
      if (!missingSides) return;

      const newWalls = missingSides.map((side) =>
        createWall(side.start, side.end)
      );
      setShapes((prev) => [...prev, ...newWalls]);
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.closeWallRect],
  );

  useHotkeys(
    shortcuts.shapeSelection.flipHorizontal,
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const allShapes = selectedIds
        .map((id) => shapesRef.current.find((s) => s.id === id))
        .filter(Boolean) as Shape[];
      if (allShapes.length === 0) return;

      if (allShapes.length === 1) {
        // Single shape: flip around its own center
        const s = allShapes[0];
        if (s.type === "spot") return;
        const newScaleX = (s.scaleX || 1) * -1;
        updateShape(s.id, {
          scaleX: newScaleX,
          x: s.x + (newScaleX < 0 ? s.width : -s.width)
        });
      } else {
        // Multi-selection: flip as a group around group bounding box
        let minX = Infinity;
        let maxX = -Infinity;
        for (const s of allShapes) {
          const flipped = (s.scaleX || 1) < 0;
          minX = Math.min(minX, flipped ? s.x - s.width : s.x);
          maxX = Math.max(maxX, flipped ? s.x : s.x + s.width);
        }
        for (const s of allShapes) {
          if (s.type === "spot") {
            updateShape(s.id, { x: minX + maxX - s.x - s.width });
          } else {
            const newScaleX = (s.scaleX || 1) * -1;
            updateShape(s.id, { scaleX: newScaleX, x: minX + maxX - s.x });
          }
        }
      }
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.flipHorizontal],
  );

  useHotkeys(
    shortcuts.shapeSelection.flipVertical,
    (event) => {
      event.preventDefault();
      event.stopPropagation();

      const allShapes = selectedIds
        .map((id) => shapesRef.current.find((s) => s.id === id))
        .filter(Boolean) as Shape[];
      if (allShapes.length === 0) return;

      if (allShapes.length === 1) {
        const s = allShapes[0];
        if (s.type === "spot") return;
        const newScaleY = (s.scaleY || 1) * -1;
        updateShape(s.id, {
          scaleY: newScaleY,
          y: s.y + (newScaleY < 0 ? s.height : -s.height)
        });
      } else {
        let minY = Infinity;
        let maxY = -Infinity;
        for (const s of allShapes) {
          const flipped = (s.scaleY || 1) < 0;
          minY = Math.min(minY, flipped ? s.y - s.height : s.y);
          maxY = Math.max(maxY, flipped ? s.y : s.y + s.height);
        }
        for (const s of allShapes) {
          if (s.type === "spot") {
            updateShape(s.id, { y: minY + maxY - s.y - s.height });
          } else {
            const newScaleY = (s.scaleY || 1) * -1;
            updateShape(s.id, { scaleY: newScaleY, y: minY + maxY - s.y });
          }
        }
      }
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.flipVertical],
  );

  useHotkeys(
    shortcuts.shapeSelection.eyedropper,
    (event) => {
      // ctrl+c triggers eyedropper only on Mac; on other platforms it's copy
      const isMac = navigator.platform.toUpperCase().includes("MAC");
      if (event.ctrlKey && !isMac) return;
      event.preventDefault();
      event.stopPropagation();
      if (!window.EyeDropper) return;
      const dropper = new window.EyeDropper();
      dropper.open().then((result) => {
        const color = result.sRGBHex;
        selectedIds.forEach((id) => {
          const shape = shapesRef.current.find((s) => s.id === id);
          if (!shape) return;
          if (shape.type === "spot") {
            updateShape(id, { color, colorSource: "manual" });
          } else {
            updateShape(id, { color });
          }
        });
      }).catch(() => {
        // User cancelled the eyedropper
      });
    },
    shortcutOptions,
    [selectedIds, shortcuts.shapeSelection.eyedropper],
  );
};
